import { defineStore } from 'pinia'
import Network from '@/Network'
import type { WebNotification } from '@/types/web-notification.types'

export interface NotificationState {
  notifications: WebNotification[]
  unreadCount: number
  totalCount: number
  isLoading: boolean
  currentPage: number
  pageSize: number
  hasMore: boolean
  pollingIntervalId: ReturnType<typeof setInterval> | null
}

export const useNotificationStore = defineStore('notification', {
  state: (): NotificationState => ({
    notifications: [],
    unreadCount: 0,
    totalCount: 0,
    isLoading: false,
    currentPage: 1,
    pageSize: 20,
    hasMore: true,
    pollingIntervalId: null
  }),

  getters: {
    hasUnread: (state) => state.unreadCount > 0
  },

  actions: {
    async fetchNotifications(page = 1) {
      this.isLoading = true
      try {
        const response = await Network.webNotification.list({
          page,
          limit: this.pageSize
        })
        this.notifications = response.data
        this.totalCount = response.count
        this.unreadCount = response.unreadCount
        this.currentPage = page
        this.hasMore = response.data.length === this.pageSize
      } catch (e) {
        console.error('Erro ao buscar notificações', e)
      } finally {
        this.isLoading = false
      }
    },

    async loadMore() {
      if (!this.hasMore || this.isLoading) return
      this.isLoading = true
      try {
        const nextPage = this.currentPage + 1
        const response = await Network.webNotification.list({
          page: nextPage,
          limit: this.pageSize
        })
        this.notifications.push(...response.data)
        this.totalCount = response.count
        this.unreadCount = response.unreadCount
        this.currentPage = nextPage
        this.hasMore = response.data.length === this.pageSize
      } catch (e) {
        console.error('Erro ao carregar mais notificações', e)
      } finally {
        this.isLoading = false
      }
    },

    async fetchUnreadCount() {
      try {
        const response = await Network.webNotification.getSummary()
        this.unreadCount = response.unreadCount
      } catch {
        // silently fail — polling should not break the UI
      }
    },

    async markAsRead(id: number) {
      try {
        await Network.webNotification.markAsRead(id)
        const notification = this.notifications.find((n) => n.id === id)
        if (notification && !notification.isRead) {
          notification.isRead = true
          this.unreadCount = Math.max(0, this.unreadCount - 1)
        }
      } catch (e) {
        console.error('Erro ao marcar notificação como lida', e)
      }
    },

    async markAllAsRead() {
      try {
        await Network.webNotification.markAllAsRead()
        this.notifications.forEach((n) => {
          n.isRead = true
        })
        this.unreadCount = 0
      } catch (e) {
        console.error('Erro ao marcar todas como lidas', e)
      }
    },

    startPolling(intervalMs = 60000) {
      this.stopPolling()
      this.fetchUnreadCount()
      this.pollingIntervalId = setInterval(() => {
        this.fetchUnreadCount()
      }, intervalMs)
    },

    stopPolling() {
      if (this.pollingIntervalId) {
        clearInterval(this.pollingIntervalId)
        this.pollingIntervalId = null
      }
    }
  }
})
